export const PROGRESS_STORAGE_KEY = "wcar-ai-course-progress";
export const PROGRESS_VERSION = 5;

export type ActivityState = Record<string, unknown>;

export type AssessmentAttempt = {
  attemptedAt: string;
  score: number;
  questionIds: string[];
};

export type CourseProgress = {
  version: number;
  started: boolean;
  completedModuleIds: string[];
  activities: Record<string, ActivityState>;
  assessmentSeed: number;
  assessmentAttempts: AssessmentAttempt[];
  passedAt?: string;
};

export const initialProgress: CourseProgress = {
  version: PROGRESS_VERSION,
  started: false,
  completedModuleIds: [],
  activities: {},
  assessmentSeed: 1,
  assessmentAttempts: [],
};

export type ProgressLoadResult = {
  progress: CourseProgress;
  storageAvailable: boolean;
};

export function normalizeProgress(candidate: unknown): CourseProgress {
  if (!candidate || typeof candidate !== "object") {
    return structuredClone(initialProgress);
  }

  const value = candidate as Partial<CourseProgress>;
  if (value.version !== PROGRESS_VERSION) {
    return structuredClone(initialProgress);
  }

  return {
    version: PROGRESS_VERSION,
    started: value.started === true,
    completedModuleIds: Array.isArray(value.completedModuleIds)
      ? value.completedModuleIds.filter(
          (item): item is string => typeof item === "string",
        )
      : [],
    activities:
      value.activities && typeof value.activities === "object"
        ? value.activities
        : {},
    assessmentSeed:
      typeof value.assessmentSeed === "number" ? value.assessmentSeed : 1,
    assessmentAttempts: Array.isArray(value.assessmentAttempts)
      ? value.assessmentAttempts
      : [],
    passedAt:
      typeof value.passedAt === "string" ? value.passedAt : undefined,
  };
}

export function loadProgress(storage: Pick<Storage, "getItem">): ProgressLoadResult {
  try {
    const raw = storage.getItem(PROGRESS_STORAGE_KEY);
    return {
      progress: raw
        ? normalizeProgress(JSON.parse(raw))
        : structuredClone(initialProgress),
      storageAvailable: true,
    };
  } catch {
    return {
      progress: structuredClone(initialProgress),
      storageAvailable: false,
    };
  }
}

export function saveProgress(
  storage: Pick<Storage, "setItem">,
  progress: CourseProgress,
): boolean {
  try {
    storage.setItem(PROGRESS_STORAGE_KEY, JSON.stringify(progress));
    return true;
  } catch {
    return false;
  }
}

export function markModuleComplete(
  progress: CourseProgress,
  moduleId: string,
): CourseProgress {
  if (progress.completedModuleIds.includes(moduleId)) {
    return progress;
  }
  return {
    ...progress,
    completedModuleIds: [...progress.completedModuleIds, moduleId],
  };
}

export function setActivityState(
  progress: CourseProgress,
  moduleId: string,
  state: ActivityState,
): CourseProgress {
  return {
    ...progress,
    activities: {
      ...progress.activities,
      [moduleId]: state,
    },
  };
}

export function resetProgress(): CourseProgress {
  return structuredClone(initialProgress);
}
